var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { EveVideoPlayer } from "@adm-media/evejs-react-player";
import Collapse from "../../ui/Collapse/Collapse";
import useBreakpoint from "../../../hooks/useBreakpoint";
import getDomain from "../../../utils/get-domain";
import TrackWizard, { VERDICT_LABEL, WIZARD_STEPS, wizardVerdict } from "./TrackWizard";
import { useStreamDiagnostics } from "./useStreamDiagnostics";
import { describePath, detectCapabilities, selectableTextTracks, unnamedTextTrackCount, unobservable } from "./playbackCapabilities";
import "./PlayerDiagnostics.scss";
function formatClock(seconds) {
    var total = Math.max(0, Math.floor(seconds));
    var hours = Math.floor(total / 3600);
    var minutes = "".concat(Math.floor((total % 3600) / 60)).padStart(2, "0");
    var secs = "".concat(total % 60).padStart(2, "0");
    return hours > 0 ? "".concat(hours, ":").concat(minutes, ":").concat(secs) : "".concat(minutes, ":").concat(secs);
}
function formatBitrate(bitsPerSecond) {
    if (bitsPerSecond === undefined)
        return "n/a";
    if (bitsPerSecond >= 1000000)
        return "".concat((bitsPerSecond / 1000000).toFixed(2), " Mbps");
    return "".concat((bitsPerSecond / 1000).toFixed(0), " kbps");
}
/**
 * Seconds of media the player pulls per second spent inside segment requests,
 * over the session so far. Above 1x it is gaining ground on playback, which is
 * all it needs to do.
 *
 * Comes from the player's own delivery counters rather than from any bitrate,
 * because both available bitrates mislead: the manifest's declared BANDWIDTH is
 * a PEAK, and the measured figure is one segment's round trip.
 */
function fetchRate(diagnostics) {
    var _a;
    return (_a = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.stats) === null || _a === void 0 ? void 0 : _a.fetchRate;
}
/** What the stream actually costs, measured from the bytes delivered so far. */
function contentBitrate(diagnostics) {
    var _a;
    return (_a = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.stats) === null || _a === void 0 ? void 0 : _a.contentBitrate;
}
function failedRequests(diagnostics) {
    var stats = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.stats;
    if (!stats)
        return 0;
    return stats.requestsErrored + stats.requestsTimedout;
}
function droppedRatio(diagnostics) {
    if (!(diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.totalFrames) || diagnostics.droppedFrames === undefined)
        return undefined;
    return diagnostics.droppedFrames / diagnostics.totalFrames;
}
function buildChecklist(diagnostics, session, player, wizard, capabilities) {
    var _a, _b;
    var rows = [];
    if (capabilities.known) {
        rows.push(capabilities.delivery
            ? { label: "Adaptive streaming active (MSE)", verdict: "ok" }
            : {
                // Not a fault and not a pass: this browser plays the stream
                // itself, so the figures below are simply not collectable
                // here. Saying which ones keeps the panel honest.
                label: "Native playback: ".concat(unobservable(capabilities).join(", "), " not reported to the page"),
                verdict: "info"
            });
    }
    var audioCount = (_a = player === null || player === void 0 ? void 0 : player.getAudioTracks().length) !== null && _a !== void 0 ? _a : 0;
    rows.push(audioCount >= 2
        ? { label: "Audio: ".concat(audioCount, " languages offered"), verdict: "ok" }
        : { label: "Audio: single track, no language choice", verdict: "info" });
    // Unnamed tracks are left out: on Safari that is the synthesised caption
    // track for a stream with no subtitles, which the player's own menu hides.
    var textCount = selectableTextTracks(player).length;
    rows.push(textCount > 0 ? { label: "Subtitles: ".concat(textCount, " track(s) offered"), verdict: "ok" } : { label: "Subtitles: none offered", verdict: "info" });
    var levels = (_b = player === null || player === void 0 ? void 0 : player.getQualityLevels().filter(function (level) { return !level.isAuto; }).length) !== null && _b !== void 0 ? _b : 0;
    if (!capabilities.renditions) {
        // The stream may well carry several renditions and be switching between
        // them: this browser just does not say so. Reporting the silence as a
        // single rendition, or as a clean adaptive run, would both be inventions.
        rows.push({ label: "ABR: not observable on this browser's native playback", verdict: "info" });
    }
    else if (levels < 2) {
        rows.push({ label: "ABR: single rendition, no adaptation possible", verdict: "info" });
    }
    else if (session.abrDownSwitches > 0) {
        // The player choosing a cheaper rendition is the one throughput signal worth acting on.
        rows.push({ label: "ABR: ".concat(levels, " renditions, quality stepped down ").concat(session.abrDownSwitches, " time(s)"), verdict: "warn" });
    }
    else {
        rows.push({ label: "ABR: ".concat(levels, " renditions, ").concat(session.abrSwitches, " switch(es), none downward"), verdict: "ok" });
    }
    var rate = fetchRate(diagnostics);
    if (rate !== undefined) {
        rows.push(rate >= 1
            ? { label: "Fetching ".concat(rate.toFixed(1), "x faster than real time"), verdict: "ok" }
            : { label: "Fetching at ".concat(rate.toFixed(1), "x real time, slower than playback consumes"), verdict: "warn" });
    }
    var failures = failedRequests(diagnostics);
    if (failures > 0) {
        rows.push({ label: "".concat(failures, " segment request(s) failed or timed out"), verdict: "fail" });
    }
    var dropped = droppedRatio(diagnostics);
    if (dropped !== undefined) {
        rows.push({
            label: "Dropped frames ".concat((dropped * 100).toFixed(2), "%"),
            verdict: dropped < 0.005 ? "ok" : dropped < 0.02 ? "warn" : "fail"
        });
    }
    rows.push(session.rebufferCount === 0
        ? { label: "No rebuffering in ".concat(formatClock(session.uptimeSeconds)), verdict: "ok" }
        : {
            label: "".concat(session.rebufferCount, " rebuffer(s), ").concat(session.rebufferSeconds.toFixed(1), "s total"),
            verdict: session.rebufferSeconds > 2 ? "fail" : "warn"
        });
    // The measured verdicts on the line are the point of the guided run, so they
    // are repeated here whatever their outcome.
    ["bandwidth", "stability"].forEach(function (id) {
        var result = wizard[id];
        if (!(result === null || result === void 0 ? void 0 : result.detail))
            return;
        var passed = result.state === "done";
        var degraded = result.state === "warn";
        var verdict = passed ? "ok" : degraded ? "warn" : result.state === "unavailable" ? "info" : "fail";
        rows.push({ label: result.detail, verdict: verdict });
    });
    // Anything else the guided checks did not confirm is worth surfacing too.
    Object.entries(wizard)
        .filter(function (_a) {
        var id = _a[0], result = _a[1];
        return id !== "bandwidth" && id !== "stability" && ((result === null || result === void 0 ? void 0 : result.state) === "missed" || (result === null || result === void 0 ? void 0 : result.state) === "warn");
    })
        .forEach(function (_a) {
        var _b;
        var id = _a[0], result = _a[1];
        rows.push({ label: "Guided check \"".concat(id, "\": ").concat((_b = result === null || result === void 0 ? void 0 : result.detail) !== null && _b !== void 0 ? _b : "not confirmed"), verdict: (result === null || result === void 0 ? void 0 : result.state) === "warn" ? "warn" : "fail" });
    });
    return rows;
}
/** Short tag per step state, so a pasted report scans at a glance. */
var REPORT_STATE_TAG = {
    done: "ok",
    warn: "warn",
    failed: "FAIL",
    missed: "FAIL",
    unavailable: "n/a",
    waiting: "not run",
    active: "not run"
};
/**
 * Builds the plain-text report the tester copies and emails over.
 *
 * Plain text on purpose: it survives every mail client, where a table or JSON
 * would not. It also keeps to the same rule as the panel and carries nothing
 * that would reveal the source is a looping VOD behind a live UI, so no URL,
 * duration or playlist type. The team already knows which stream /videotest
 * serves; what they cannot know is what it did on this viewer's machine.
 */
export function buildDiagnosticsReport(input) {
    var _a, _b, _c, _d, _e, _f;
    var diagnostics = input.diagnostics, session = input.session, player = input.player, wizard = input.wizard, now = input.now, playerSize = input.playerSize, capabilities = input.capabilities;
    var lines = [];
    lines.push("EVE player test report");
    lines.push("Generated: ".concat(now.toISOString()));
    lines.push("Verdict: ".concat(VERDICT_LABEL[wizardVerdict(wizard)]));
    lines.push("");
    lines.push("Checks");
    for (var _i = 0, WIZARD_STEPS_1 = WIZARD_STEPS; _i < WIZARD_STEPS_1.length; _i++) {
        var step = WIZARD_STEPS_1[_i];
        var result = wizard[step.id];
        var tag = REPORT_STATE_TAG[(_a = result === null || result === void 0 ? void 0 : result.state) !== null && _a !== void 0 ? _a : "waiting"];
        lines.push("  [".concat(tag, "] ").concat(step.title, ": ").concat((_b = result === null || result === void 0 ? void 0 : result.detail) !== null && _b !== void 0 ? _b : "not run"));
        // In interactive mode the tester's own answer outranks the event that
        // triggered it, so it is spelled out under the check rather than folded
        // into the detail line.
        if (result === null || result === void 0 ? void 0 : result.confirmed) {
            lines.push("        Tester answered: ".concat(result.confirmed).concat(result.note ? ". Note: ".concat(result.note) : ""));
        }
    }
    lines.push("");
    var disputed = WIZARD_STEPS.filter(function (step) { var _a; return ((_a = wizard[step.id]) === null || _a === void 0 ? void 0 : _a.confirmed) === "no"; });
    if (disputed.length > 0) {
        lines.push("Reported as not working by the tester");
        for (var _g = 0, disputed_1 = disputed; _g < disputed_1.length; _g++) {
            var step = disputed_1[_g];
            var result = wizard[step.id];
            lines.push("  ".concat(step.title).concat((result === null || result === void 0 ? void 0 : result.note) ? ": ".concat(result.note) : ""));
        }
        lines.push("");
    }
    var rate = fetchRate(diagnostics);
    // Everything the streaming engine measures is missing rather than zero on a
    // browser that plays the stream itself, and a support inbox reading "Segment
    // errors: 0" off a Safari report would take it for a clean line.
    var engineless = !!capabilities && capabilities.known && !capabilities.delivery;
    var fromEngine = function (value) { return (engineless ? "not reported by this browser" : value); };
    lines.push("Line");
    lines.push("  On air for: ".concat(formatClock(session.uptimeSeconds)));
    lines.push("  Content bitrate: ".concat(fromEngine(formatBitrate(contentBitrate(diagnostics)))));
    lines.push("  Measured bandwidth: ".concat(fromEngine(formatBitrate((_c = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.stats) === null || _c === void 0 ? void 0 : _c.measuredBandwidth))));
    lines.push("  Fetch rate: ".concat(fromEngine(rate !== undefined ? "".concat(rate.toFixed(1), "x real time") : "n/a")));
    lines.push("  Resolution: ".concat(diagnostics && diagnostics.width ? "".concat(diagnostics.width, "x").concat(diagnostics.height) : "n/a"));
    lines.push("  Frame rate: ".concat((diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.fps) !== undefined ? "".concat(diagnostics.fps, " fps") : "n/a"));
    lines.push("  Dropped frames: ".concat(diagnostics && diagnostics.droppedFrames !== undefined && diagnostics.totalFrames !== undefined
        ? "".concat(diagnostics.droppedFrames, " / ").concat(diagnostics.totalFrames)
        : "n/a"));
    lines.push("  Rebuffering: ".concat(session.rebufferCount, " x ").concat(session.rebufferSeconds.toFixed(1), "s"));
    lines.push("  ABR switches: ".concat(fromEngine("".concat(session.abrSwitches, " (").concat(session.abrDownSwitches, " down)"))));
    lines.push("  Segment errors: ".concat(fromEngine("".concat(failedRequests(diagnostics)))));
    lines.push("  Codecs: ".concat(fromEngine((_d = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.codecs) !== null && _d !== void 0 ? _d : "n/a")));
    lines.push("");
    var audio = (_e = player === null || player === void 0 ? void 0 : player.getAudioTracks().length) !== null && _e !== void 0 ? _e : 0;
    var text = selectableTextTracks(player).length;
    // Named rather than silently dropped: an unnamed native caption track is
    // itself a clue when a tester reports a stray subtitle entry.
    var unnamed = unnamedTextTrackCount(player);
    var levels = (_f = player === null || player === void 0 ? void 0 : player.getQualityLevels().filter(function (level) { return !level.isAuto; }).length) !== null && _f !== void 0 ? _f : 0;
    lines.push("Source");
    lines.push("  Audio tracks: ".concat(audio));
    lines.push("  Subtitle tracks: ".concat(text).concat(unnamed > 0 ? " (+".concat(unnamed, " unnamed native track(s) ignored)") : ""));
    lines.push("  Renditions: ".concat(fromEngine("".concat(levels))));
    lines.push("  Playback path: ".concat(capabilities ? describePath(capabilities) : (diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.stats) ? "adaptive streaming (MSE)" : "native"));
    lines.push("");
    lines.push("Environment");
    // The same domain the app identifies the event by, `?domain=` override
    // included, so a report from a staging host names the event it was really
    // testing rather than the machine it ran on.
    lines.push("  Event domain: ".concat(getDomain() || "unknown"));
    lines.push("  Player: ".concat(EveVideoPlayer.version));
    lines.push("  Browser: ".concat(typeof navigator !== "undefined" ? navigator.userAgent : "unknown"));
    // Two different sizes, both worth having. The player box against the stream
    // resolution says whether the rendition being served suits the space it is
    // drawn in; the browser viewport and pixel ratio say what the device is.
    lines.push("  Player size: ".concat(playerSize ? "".concat(playerSize.width, "x").concat(playerSize.height, " CSS px") : "not measured"));
    lines.push("  Browser viewport: ".concat(typeof window !== "undefined"
        ? "".concat(window.innerWidth, "x").concat(window.innerHeight, " CSS px at ").concat(window.devicePixelRatio || 1, "x pixel ratio")
        : "unknown"));
    // Spelled out so a Safari report is not read as a stream with one rendition
    // and no throughput: those figures were never available to measure.
    var missing = capabilities ? unobservable(capabilities) : [];
    if (missing.length > 0)
        lines.push("  Not observable in this browser: ".concat(missing.join(", ")));
    return lines.join("\n");
}
var PlayerDiagnostics = function (_a) {
    var _b, _c;
    var player = _a.player, containerRef = _a.containerRef, simulate = _a.simulate, interactive = _a.interactive, supportEmail = _a.supportEmail;
    var windowSize = useBreakpoint().windowSize;
    // Below the lg breakpoint the panel sits under the player, so the detail
    // sections start collapsed and the wizard stays the first thing in reach.
    var isStacked = windowSize.width < 992;
    var _d = useStreamDiagnostics(containerRef, player, true), diagnostics = _d.diagnostics, session = _d.session;
    // What this browser can actually report, re-read on every sample: the panel
    // and the report both have to distinguish a bad reading from no reading.
    var capabilities = detectCapabilities(diagnostics, containerRef.current);
    var _e = useState({}), wizardResults = _e[0], setWizardResults = _e[1];
    var onResultsChange = useCallback(function (results) { return setWizardResults(results); }, []);
    // Built at click time rather than kept in state, so the report carries the
    // latest sample instead of whatever was current when the run finished.
    // Names the event and the outcome, so a support inbox can sort the mail
    // before anyone opens it.
    var reportSubject = "Player test report: ".concat(getDomain() || "unknown domain", " (").concat(VERDICT_LABEL[wizardVerdict(wizardResults)], ")");
    var buildReport = useCallback(function () {
        var _a;
        // Measured at click time off the element the player was mounted into,
        // since the player exposes no getter for its rendered box.
        var box = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        var playerSize = box && box.width > 0 ? { width: Math.round(box.width), height: Math.round(box.height) } : undefined;
        return buildDiagnosticsReport({ diagnostics: diagnostics, session: session, player: player, wizard: wizardResults, now: new Date(), playerSize: playerSize, capabilities: capabilities });
    }, [diagnostics, session, player, wizardResults, containerRef, capabilities]);
    var rate = fetchRate(diagnostics);
    // On the native path these figures do not exist rather than read zero, and a
    // row of "n/a" reads as a broken panel. Say who is not reporting them.
    var engineless = capabilities.known && !capabilities.delivery;
    var fromEngine = function (value) { return (engineless ? "not reported by this browser" : value); };
    var metrics = [
        ["On air for", formatClock(session.uptimeSeconds)],
        // Static, read off the bundled build: doubles as a check of which player
        // version this deploy actually loaded.
        ["Player version", EveVideoPlayer.version],
        ["Resolution", diagnostics && diagnostics.width ? "".concat(diagnostics.width, "x").concat(diagnostics.height) : "n/a"],
        ["Frame rate", (diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.fps) !== undefined ? "".concat(diagnostics.fps, " fps") : "n/a"],
        ["Content bitrate", fromEngine(formatBitrate(contentBitrate(diagnostics)))],
        ["Measured bandwidth", fromEngine(formatBitrate((_b = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.stats) === null || _b === void 0 ? void 0 : _b.measuredBandwidth))],
        ["Fetch rate", fromEngine(rate !== undefined ? "".concat(rate.toFixed(1), "x real time") : "n/a")],
        ["Segment errors", fromEngine("".concat(failedRequests(diagnostics)))],
        [
            "Dropped frames",
            diagnostics && diagnostics.droppedFrames !== undefined && diagnostics.totalFrames !== undefined
                ? "".concat(diagnostics.droppedFrames, " / ").concat(diagnostics.totalFrames)
                : "n/a"
        ],
        ["Rebuffering", "".concat(session.rebufferCount, " x ").concat(session.rebufferSeconds.toFixed(1), "s")],
        ["ABR switches", fromEngine("".concat(session.abrSwitches, " (").concat(session.abrDownSwitches, " down)"))],
        ["Codecs", fromEngine((_c = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.codecs) !== null && _c !== void 0 ? _c : "n/a")]
    ];
    var checklist = buildChecklist(diagnostics, session, player, wizardResults, capabilities);
    var metricsSection = (_jsxs(_Fragment, { children: [_jsx("dl", __assign({ className: "player-diagnostics__metrics", "data-testid": "diagnostics-metrics" }, { children: metrics.map(function (_a) {
                    var label = _a[0], value = _a[1];
                    return (_jsxs("div", __assign({ className: "player-diagnostics__metric" }, { children: [_jsx("dt", { children: label }), _jsx("dd", { children: value })] }), label));
                }) })), engineless && (_jsx("p", __assign({ className: "player-diagnostics__note", "data-testid": "diagnostics-native-note" }, { children: "This browser plays the stream itself instead of through the page's streaming engine, so throughput, renditions and codecs are never handed over. Sound, languages, subtitles, resolution, dropped frames and rebuffering are all still measured here." }))), _jsx("p", __assign({ className: "player-diagnostics__note" }, { children: "Fetch rate is what the line is judged on: seconds of media pulled per second spent downloading, so it needs no bitrate to be meaningful. Content bitrate is measured from the bytes actually delivered, not from the manifest, whose BANDWIDTH is a declared peak and runs higher. Measured bandwidth is only the last segment's round trip and is the noisiest figure here." }))] }));
    var checklistSection = (_jsx("ul", __assign({ className: "player-diagnostics__checklist", "data-testid": "diagnostics-checklist" }, { children: checklist.map(function (row) { return (_jsx("li", __assign({ className: "player-diagnostics__check player-diagnostics__check--".concat(row.verdict) }, { children: row.label }), row.label)); }) })));
    return (_jsxs("aside", __assign({ className: "player-diagnostics", "data-testid": "player-diagnostics" }, { children: [_jsxs("div", __assign({ className: "player-diagnostics__section" }, { children: [_jsx("h2", __assign({ className: "player-diagnostics__title" }, { children: "Guided checks" })), _jsx(TrackWizard, { player: player, containerRef: containerRef, diagnostics: diagnostics, session: session, onResultsChange: onResultsChange, buildReport: buildReport, simulate: simulate, interactive: interactive, reportSubject: reportSubject, supportEmail: supportEmail })] })), isStacked ? (_jsxs(Collapse, __assign({ className: "player-diagnostics__collapse", defaultActiveKey: ["line"] }, { children: [_jsx(Collapse.Panel, __assign({ header: "Line analysis" }, { children: metricsSection }), "line"), _jsx(Collapse.Panel, __assign({ header: "Summary" }, { children: checklistSection }), "summary")] }))) : (_jsxs(_Fragment, { children: [_jsxs("div", __assign({ className: "player-diagnostics__section" }, { children: [_jsx("h2", __assign({ className: "player-diagnostics__title" }, { children: "Line analysis" })), metricsSection] })), _jsxs("div", __assign({ className: "player-diagnostics__section" }, { children: [_jsx("h2", __assign({ className: "player-diagnostics__title" }, { children: "Summary" })), checklistSection] }))] }))] })));
};
export default PlayerDiagnostics;
